import type { AlertColor } from '@mui/material';

import { formatDateISOInSydney } from '~/services/TimeService';

import type {
  CampaignScheduleDependencyViolation,
  CampaignScheduleImportCandidate,
  CampaignScheduleImportViolation,
} from './types';

const MISSING_TYPE_LABELS: Record<string, string> = {
  availability: 'a purchase (availability) schedule',
  online_purchase: 'an Available Offline schedule',
};

const SCHEDULE_TYPE_LABELS: Record<string, string> = {
  availability: 'Book By Dates',
  online_purchase: 'Available Offline',
  list_visibility: 'List Visibility',
  lux_plus: 'LuxPlus+',
};

const EDITABLE_ON_THIS_UI_CTA = 'You can still edit on this UI.';

export function severityToAlertColor(severity: CampaignScheduleImportViolation['severity']): AlertColor {
  if (severity === 'blocking') {
    return 'error';
  }
  return severity;
}

// Field presence per `code` isn't compiler-enforced — see svc-offer's
// docs/campaign-schedule-import-business-rules.md#violation-contract-shape.
export function formatViolation(violation: CampaignScheduleImportViolation): string {
  switch (violation.code) {
    case 'opportunity_book_by_missing':
      return 'Opportunity.Book_By__c is not set — availability schedules cannot be derived';
    case 'no_start_online_set':
      return 'No Salesforce campaign schedule has a start_online__c set — availability and online_purchase schedules cannot be derived';
    case 'no_offline_type_record':
      return 'No offline-type (e.g. "Offline Period") Salesforce campaign schedule found — cannot derive an Available Offline (online_purchase) end date';
    case 'multiple_offline_type_records':
      return `Multiple offline-type Salesforce campaign schedules found (${violation.scheduleIds.join(
        ', ',
      )}) — cannot determine a single Available Offline end date`;
    case 'superseded_campaign_cycles': {
      const count = violation.scheduleIds.length;
      return `${count} Salesforce ${count === 1 ? 'schedule' : 'schedules'} from earlier re-open cycles ${count === 1 ? 'is' : 'are'} superseded and won't be imported`;
    }
    case 'upcoming_campaign_cycles': {
      const count = violation.scheduleIds.length;
      return `${count} Salesforce ${count === 1 ? 'schedule' : 'schedules'} from later re-open cycles ${count === 1 ? 'is' : 'are'} queued — import again once the current cycle ends`;
    }
    case 'run_after_offline_period':
      return `Salesforce campaign schedule ${violation.scheduleId} starts after the Offline Period ends — if it's a new re-open, add its Offline Period in Salesforce. LE Business Traveller and LE Agent Hub schedules are skipped until then`;
    case 'schedule_missing_dates': {
      const missing = [violation.missingStart && 'start date', violation.missingEnd && 'end date']
        .filter(Boolean)
        .join(' and ');
      return `Salesforce campaign schedule ${violation.scheduleId} (${
        violation.campaignType ?? 'unknown type'
      }) is missing its ${missing} — it will not appear below until Salesforce has both dates`;
    }
    case 'schedule_missing_campaign_type':
      return `Salesforce campaign schedule ${violation.scheduleId} has no dynamic_campaign_type__c set — it will not appear below`;
    case 'unmapped_brand':
      return `Brand__c value "${violation.brandValue}" has no known svc-offer brand mapping — cannot be imported`;
    case 'unmapped_market':
      return `Market__c value "${violation.marketValue}" has no known svc-offer region mapping — cannot be imported`;
    case 'lux_plus_ineligible_market':
      return `LuxPlus+ is not available in market "${violation.market}" — schedule will not be imported`;
    case 'lux_plus_ineligible_brand':
      return `LuxPlus+ schedules are Luxury Escapes only — "${violation.brand}" will not be imported`;
    case 'invalid_region_for_brand':
      return `${violation.brand} is not sold in ${violation.market} (${violation.region}) — this schedule will not be imported`;
    case 'dependency_blocker':
      return `${violation.brandLabel} (${violation.marketLabel}): ${formatDependencyViolation(violation.dependency)}`;
    case 'missing_region_coverage':
      return [
        `${violation.brand} is missing:`,
        `- ${MISSING_TYPE_LABELS[violation.type]} for: ${violation.regions.join(', ')}`,
      ].join('\n');
    case 'legacy_schedules_page_incompatible': {
      const header = violation.isPreexisting
        ? "This offer's current schedules are incompatible with the legacy edit-offer Schedules page:"
        : 'This import will break compatibility on the legacy edit-offer Schedules page:';
      return [
        header,
        `- ${violation.brand} missing ${MISSING_TYPE_LABELS[violation.type]} for: ${violation.regions.join(', ')}`,
        EDITABLE_ON_THIS_UI_CTA,
      ].join('\n');
    }
    case 'conflicting_source_records': {
      const windows = violation.windows
        .map((window) => `${formatDateISOInSydney(window.start)} – ${formatDateISOInSydney(window.end)}`)
        .join(' vs ');
      const typeLabel = SCHEDULE_TYPE_LABELS[violation.type] ?? violation.type;
      return `${violation.brandLabel} (${violation.marketLabel}): ${violation.recordCount} Salesforce records target the same ${typeLabel} schedule with different dates (${windows})`;
    }
    case 'app_exclusive_overlap': {
      const windows = (violation.windows ?? [])
        .map((window) => `${formatDateISOInSydney(window.start)} – ${formatDateISOInSydney(window.end)}`)
        .join(', ');
      const where = `${violation.brandLabel} (${violation.marketLabel})`;
      const overlap = windows ? ` (${windows})` : '';
      return `${where}: List Visibility overlaps an App Exclusive schedule${overlap}. The offer could appear on the front page during that window.`;
    }
    case 'reopen_would_end_live_schedule': {
      const typeLabel = SCHEDULE_TYPE_LABELS[violation.type] ?? violation.type;
      return `${violation.brandLabel} (${violation.marketLabel}): ${typeLabel} is live until ${formatDateISOInSydney(violation.liveWindow.end)} — importing the next re-open now would take it off the site. Import it once the current run ends.`;
    }
    case 'post_import_dependency_violation': {
      const dependentLabel = SCHEDULE_TYPE_LABELS[violation.dependency.candidateType];
      const requiredLabel = SCHEDULE_TYPE_LABELS[violation.dependency.requiredType];
      const where = `${violation.brandLabel} (${violation.marketLabel})`;
      const isMissing = violation.dependency.code === 'missing_dependency_schedule';

      if (violation.isSelected) {
        return isMissing
          ? `${where}: ${dependentLabel} requires a ${requiredLabel} schedule`
          : `${where}: ${dependentLabel} dates must fall within the ${requiredLabel} dates once this import is applied`;
      }
      return isMissing
        ? `${where}: the existing ${dependentLabel} schedule is not part of this import and would be left without a ${requiredLabel} schedule`
        : `${where}: the existing ${dependentLabel} schedule is not part of this import and would fall outside the new ${requiredLabel} dates`;
    }
    default: {
      // Exhaustiveness check on `code` — a new code without a case here is a compile error.
      const unreachable: never = violation.code;
      return unreachable;
    }
  }
}

export function formatDependencyViolation(dependency: CampaignScheduleDependencyViolation): string {
  if (dependency.code === 'missing_dependency_schedule') {
    return `Requires an existing or selected ${dependency.requiredType} schedule`;
  }
  return `${dependency.candidateType} dates must fall within the ${dependency.requiredType} schedule's dates`;
}

// See README.md#violation-display-rules.
export function deriveCandidateDependencyViolations(
  candidates: Array<CampaignScheduleImportCandidate>,
): Array<CampaignScheduleImportViolation> {
  const violations: Array<CampaignScheduleImportViolation> = [];

  candidates.forEach((candidate) => {
    if (!candidate.dependencyViolation) {
      return;
    }

    violations.push({
      code: 'dependency_blocker',
      severity: 'blocking',
      brand: candidate.brand,
      brandLabel: candidate.brandLabel,
      region: candidate.region,
      marketLabel: candidate.marketLabel,
      dependency: candidate.dependencyViolation,
    });
  });

  return violations;
}

export interface GroupedViolation {
  key: string;
  severity: CampaignScheduleImportViolation['severity'];
  text: string;
}

// See README.md#violation-display-rules.
export function groupViolations(violations: Array<CampaignScheduleImportViolation>): Array<GroupedViolation> {
  const grouped: Array<GroupedViolation> = [];
  const luxPlusMarkets: Array<string> = [];
  const luxPlusBrands: Array<string> = [];
  const invalidRegionsByBrand = new Map<string, Array<string>>();
  const legacyIncompatibleViolations: Array<CampaignScheduleImportViolation> = [];
  const dependencyGroups = new Map<string, { dependency: CampaignScheduleDependencyViolation; count: number }>();

  violations.forEach((violation) => {
    switch (violation.code) {
      case 'lux_plus_ineligible_market':
        luxPlusMarkets.push(violation.market);
        return;
      case 'lux_plus_ineligible_brand':
        luxPlusBrands.push(violation.brand);
        return;
      case 'invalid_region_for_brand': {
        const markets = invalidRegionsByBrand.get(violation.brand) ?? [];
        markets.push(`${violation.market} (${violation.region})`);
        invalidRegionsByBrand.set(violation.brand, markets);
        return;
      }
      case 'legacy_schedules_page_incompatible':
        legacyIncompatibleViolations.push(violation);
        return;
      case 'dependency_blocker': {
        const dependency = violation.dependency;
        const key = `${dependency.code}::${dependency.candidateType}::${dependency.requiredType}`;
        const existing = dependencyGroups.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          dependencyGroups.set(key, { dependency, count: 1 });
        }
        return;
      }
      default:
        grouped.push({
          key: `${violation.code}-${grouped.length}`,
          severity: violation.severity,
          text: formatViolation(violation),
        });
    }
  });

  if (luxPlusMarkets.length > 0) {
    const markets = Array.from(new Set(luxPlusMarkets));
    grouped.push({
      key: 'lux_plus_ineligible_market',
      severity: 'info',
      text: `LuxPlus+ is not available in ${markets.length} market${markets.length === 1 ? '' : 's'}: ${markets.join(
        ', ',
      )} — schedule${markets.length === 1 ? '' : 's'} will not be imported`,
    });
  }

  if (luxPlusBrands.length > 0) {
    const brands = Array.from(new Set(luxPlusBrands));
    grouped.push({
      key: 'lux_plus_ineligible_brand',
      severity: 'info',
      text: `LuxPlus+ schedules are Luxury Escapes only — ${brands.join(', ')} will not be imported`,
    });
  }

  const brandsBySignature = new Map<string, { markets: Array<string>; brands: Array<string> }>();
  invalidRegionsByBrand.forEach((markets, brand) => {
    const sortedMarkets = Array.from(new Set(markets)).sort();
    const signature = sortedMarkets.join('|');
    const existing = brandsBySignature.get(signature);
    if (existing) {
      existing.brands.push(brand);
    } else {
      brandsBySignature.set(signature, { markets: sortedMarkets, brands: [brand] });
    }
  });
  brandsBySignature.forEach(({ markets, brands }, signature) => {
    const isSingle = brands.length === 1 && markets.length === 1;
    grouped.push({
      key: `invalid_region_for_brand-${signature}`,
      severity: 'info',
      text: `${brands.join(', ')} ${brands.length === 1 ? 'is' : 'are'} not sold in ${markets.join(', ')} — ${
        isSingle ? 'this schedule' : 'these schedules'
      } will not be imported`,
    });
  });

  if (legacyIncompatibleViolations.length > 0) {
    // A gap caused by the pending import "wins" over a pre-existing one — if any single gap isn't
    // pre-existing, the header must say this import breaks compatibility, not just describe it.
    const isPreexisting = legacyIncompatibleViolations.every((violation) => violation.isPreexisting);
    const header = isPreexisting
      ? "This offer's current schedules are incompatible with the legacy edit-offer Schedules page:"
      : 'This import will break compatibility on the legacy edit-offer Schedules page:';
    const bullets = legacyIncompatibleViolations.map(
      (violation) =>
        `- ${violation.brand} missing ${MISSING_TYPE_LABELS[violation.type]} for: ${violation.regions.join(', ')}`,
    );
    grouped.push({
      key: 'legacy_schedules_page_incompatible',
      severity: 'warning',
      text: [header, ...bullets, EDITABLE_ON_THIS_UI_CTA].join('\n'),
    });
  }

  dependencyGroups.forEach(({ dependency, count }, key) => {
    grouped.push({
      key: `dependency_blocker-${key}`,
      severity: 'blocking',
      text: `${dependency.candidateType}: ${formatDependencyViolation(dependency)} (${count} schedule row${
        count === 1 ? '' : 's'
      } affected)`,
    });
  });

  return grouped;
}
